import type { ErrorPattern, SkillSummary } from '../../api/models';
import { AppText } from '../../components/AppText';
import { InlineNotice } from '../../components/InlineNotice';
import { PrimaryButton } from '../../components/PrimaryButton';
import { Strings } from '../../strings';

import { useDashboard } from './useDashboard';

/**
 * design-system/08-Screens.md §6 — the parent's view of one child.
 * Mirrors android/…/Screens/Dashboard/DashboardScreen.kt.
 *
 * Every sentence on it was written by the API: the trend label, each recommendation, the description of a
 * repeated mistake. The screen arranges them and writes none of its own, which is the same rule that keeps
 * "podpriemer" out of the product (BR-13).
 */
export function DashboardScreen({
  childId,
  onOpenSkill,
  onOpenAchievements,
}: {
  childId: string | null;
  onOpenSkill: (skillCode: string) => void;
  onOpenAchievements: () => void;
}) {
  const model = useDashboard(childId);
  const dashboard = model.state.dashboard;

  if (childId === null) {
    return <AppText color="var(--text-secondary)">{Strings.overviewPickChild}</AppText>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {model.state.error !== null ? (
        <>
          <InlineNotice text={model.state.error} tone="error" />
          <PrimaryButton
            label={Strings.actionRetry}
            onClick={() => void model.load()}
            height="var(--touch-parent)"
          />
        </>
      ) : null}

      {dashboard !== null ? (
        <>
          <AppText as="h1" style="titleL">
            {dashboard.nickname}
          </AppText>

          {dashboard.inCalibration ? (
            <InlineNotice text={Strings.overviewCalibrationNote} tone="info" />
          ) : null}

          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <Stat label={Strings.overviewTrainingDays} value={String(dashboard.trainingDayCount)} />
            <Stat label={Strings.overviewTotalTime} value={formatDuration(dashboard.totalTrainingTimeMs)} />
            <Stat label={Strings.overviewAttempts} value={String(dashboard.totalAttemptCount)} />
          </div>

          <AppText as="h2" style="titleM">
            {Strings.overviewSkills}
          </AppText>

          {(dashboard.skills ?? []).map((skill: SkillSummary) => (
            <SkillRow key={skill.skillCode} skill={skill} onClick={() => onOpenSkill(skill.skillCode)} />
          ))}

          {(dashboard.recommendations ?? []).length > 0 ? (
            <>
              <AppText as="h2" style="titleM">
                {Strings.overviewRecommendations}
              </AppText>

              {(dashboard.recommendations ?? []).map((recommendation, index: number) => (
                <div
                  key={index}
                  style={{
                    borderRadius: 'var(--radius-m)',
                    background: 'var(--surface-hint)',
                    padding: 'var(--space-4)',
                  }}
                >
                  <AppText color="var(--text-on-hint)">{recommendation.text}</AppText>
                </div>
              ))}
            </>
          ) : null}

          {/* A doorway rather than a section: the parent's view of achievements is the child's own
              screen, and duplicating it here would be two places to keep in step for no gain. */}
          <button
            type="button"
            onClick={onOpenAchievements}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              borderRadius: 'var(--radius-m)',
              background: 'var(--surface-raised)',
              border: 'none',
              padding: 'var(--space-4)',
              textAlign: 'left',
            }}
          >
            <AppText as="span" style="titleS" sx={{ flex: 1 }}>
              {Strings.overviewAchievements}
            </AppText>
            <AppText as="span" style="titleM" color="var(--text-secondary)">
              ›
            </AppText>
          </button>

          <AppText as="h2" style="titleM">
            {Strings.overviewErrorPatterns}
          </AppText>

          {model.state.errorPatterns.length === 0 ? (
            <AppText color="var(--text-secondary)">{Strings.overviewNoPatterns}</AppText>
          ) : (
            model.state.errorPatterns.map((pattern: ErrorPattern) => (
              <PatternRow key={pattern.errorType} pattern={pattern} />
            ))
          )}
        </>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        flex: '1 1 140px',
        borderRadius: 'var(--radius-m)',
        background: 'var(--surface-raised)',
        padding: 'var(--space-4)',
      }}
    >
      <AppText style="titleM">{value}</AppText>
      <AppText style="bodyS" color="var(--text-secondary)">
        {label}
      </AppText>
    </div>
  );
}

function SkillRow({ skill, onClick }: { skill: SkillSummary; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        borderRadius: 'var(--radius-m)',
        background: 'var(--surface-raised)',
        border: 'none',
        padding: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <AppText as="span" style="titleS" sx={{ flex: 1 }}>
          {skill.skillName}
        </AppText>
        <AppText as="span" style="titleS" color="var(--text-secondary)">
          {trendArrow(skill.trend)}
        </AppText>
        {/*
          The row has always been a button and never looked like one, so nobody knew a skill opened
          anything (R0111). A chevron, not a hover state: a finger has no hover, and divergence 12 of the
          parity register says an affordance may never depend on one.
        */}
        <AppText as="span" style="titleS" color="var(--text-secondary)" sx={{ marginLeft: 'var(--space-2)' }}>
          ›
        </AppText>
      </span>

      {/*
        A child who has closed nothing has no level (R0107). Every child is seeded at `STARTING_LEVEL`,
        so a dashboard opened seconds after "Pridať dieťa" read „Úroveň 2 z 10" with two segments filled —
        beside „0 dní tréningu, 0 min, 0 odpovedí". R0095 fixed the same lie on the achievements screen and
        not here.
      */}
      {skill.closedInstanceCount > 0 ? <LevelBar level={skill.level} maximum={skill.maximumLevel} /> : null}

      <span style={{ display: 'flex', width: '100%' }}>
        <AppText as="span" style="bodyS" color="var(--text-secondary)" sx={{ flex: 1 }}>
          {skill.closedInstanceCount > 0
            ? Strings.skillLevel(skill.level, skill.maximumLevel)
            : Strings.skillNoData}
        </AppText>
        {/* The API's own wording for the trend. It says "zatiaľ málo údajov" where a percentage would be
            misleading, and the screen must not replace that with a number it invented. */}
        <AppText as="span" style="bodyS" color="var(--text-secondary)">
          {skill.trendLabel}
        </AppText>
      </span>
    </button>
  );
}

export function LevelBar({ level, maximum }: { level: number; maximum: number }) {
  return (
    <span style={{ display: 'flex', gap: 2, width: '100%', height: 8 }}>
      {Array.from({ length: maximum }, (_unused, index: number) => (
        <span
          key={index}
          style={{
            flex: 1,
            height: 8,
            borderRadius: 'var(--radius-pill)',
            background: index < level ? 'var(--progress-fill)' : 'var(--progress-track)',
          }}
        />
      ))}
    </span>
  );
}

function PatternRow({ pattern }: { pattern: ErrorPattern }) {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-m)',
        background: 'var(--surface-raised)',
        padding: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1)',
      }}
    >
      {/* `errorType` is engine vocabulary and is deliberately not rendered anywhere — the API's sentence is
          the only description a parent sees. */}
      <AppText>{pattern.description}</AppText>
      <AppText style="bodyS" color="var(--text-secondary)">
        {pattern.occurrences}×&nbsp;&nbsp;·&nbsp;&nbsp;{Math.round(pattern.share * 100)} %
      </AppText>
    </div>
  );
}

const trendArrow = (trend: string): string =>
  trend === 'up' ? '↑' : trend === 'down' ? '↓' : trend === 'flat' ? '→' : '·';

function formatDuration(milliseconds: number): string {
  const minutes = Math.floor(milliseconds / 60_000);

  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}
